import { useRef } from 'react';

import { Box, Button, Grid, Text, useTheme } from '@simplywallst/ui-core';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { KeyMetric } from '@/components/KeyMetric';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import RefreshOutline from '@simplywallst/ui-core/icons/RefreshOutline';

import type { NarrativeValuationCompany } from '../../../../../Narrative/hooks/useNarrativeValuationCompany';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

export type Props = {
  authorDisplayName: string;
  company: NarrativeValuationCompany;
  fairValue: number;
  onReset: () => void;
};

export const NarrativeFairValue = ({
  authorDisplayName,
  company,
  fairValue,
  onReset,
}: Props) => {
  const theme = useTheme();
  const fairValueRef = useRef(fairValue);
  const currencyIso =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  return (
    <Grid
      backgroundColor="spaceGray"
      borderTopLeftRadius="x1"
      borderTopRightRadius="x1"
    >
      <Box paddingX="x2" paddingTop="x2">
        <Text as="span" typography="xSmallStrong" color="textSolid">
          Fair Value
        </Text>
      </Box>
      <Box padding="x2">
        <Grid gap={`${theme.x2spacePx}px`}>
          <KeyMetric>
            <Grid
              gap={`${theme.x2spacePx}px`}
              templateColumns="max-content auto"
              alignItems="center"
            >
              <Text typography="megaStrong" color="textSolid">
                {toCurrencyTruncated(fairValue, 1, currencyIso)}
              </Text>
              <Text typography="tiny" color="textSolid">
                <CompanyIntrinsicDiscount
                  lastSharePrice={company.analysisValue?.lastSharePrice ?? null}
                  npvPerShare={company.analysisValue?.npvPerShare ?? null}
                  fairValue={fairValue}
                />
              </Text>
            </Grid>
            <Text typography="xSmall" color="textSoft">
              {fairValueRef.current !== fairValue
                ? `You've changed Fair Value`
                : `${authorDisplayName}'s Fair Value`}
            </Text>
          </KeyMetric>
          {fairValueRef.current !== fairValue && (
            <Grid alignContent="center" justifyContent="flex-start">
              <Button
                type="button"
                styleType="gentle"
                onClick={onReset}
                alignIcon="left"
              >
                <RefreshOutline stroke={theme.color.textSolid} />
                Reset valuation
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Grid>
  );
};
